import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Search, Upload, Trash2, Plus, Ship, MapPin } from 'lucide-react';
import api from '../../utils/api';

const PortSettings = ({ formData, handleChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [newPortCode, setNewPortCode] = useState('');
  const [newPortName, setNewPortName] = useState('');
  const [importing, setImporting] = useState(false);
  const [filter, setFilter] = useState('all'); // all, TR, other

  const ports = formData.ports || [];

  // Filtrelenmiş limanlar
  const filteredPorts = ports.filter(port => {
    const matchesSearch = searchTerm === '' || 
      port.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      port.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filter === 'all' || 
      (filter === 'TR' && port.code.startsWith('TR')) ||
      (filter === 'other' && !port.code.startsWith('TR'));
    
    return matchesSearch && matchesFilter;
  });

  // Liman ekle
  const addPort = () => {
    if (newPortCode.trim() && newPortName.trim()) {
      const exists = ports.some(p => p.code === newPortCode.trim().toUpperCase());
      if (exists) {
        toast.error('Bu liman kodu zaten mevcut');
        return;
      }
      
      const updated = [...ports, { 
        code: newPortCode.trim().toUpperCase(), 
        name: newPortName.trim() 
      }].sort((a, b) => a.name.localeCompare(b.name, 'tr'));
      
      handleChange('ports', updated);
      setNewPortCode('');
      setNewPortName('');
      toast.success('Liman eklendi');
    }
  };

  // Liman sil
  const removePort = (code) => {
    if (window.confirm('Bu limanı silmek istediğinize emin misiniz?')) {
      const updated = ports.filter(p => p.code !== code);
      handleChange('ports', updated);
      toast.success('Liman silindi');
    }
  };

  // Excel'den içe aktar
  const importFromExcel = async () => {
    setImporting(true);
    try {
      const response = await api.post('/ports/import');
      toast.success(response.data.message);
      
      // Settings'i yeniden yükle
      const settingsRes = await api.get('/settings');
      if (settingsRes.data.ports) {
        handleChange('ports', settingsRes.data.ports);
      }
    } catch (error) {
      toast.error('İçe aktarma başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  // Tüm limanları temizle
  const clearAllPorts = () => {
    if (window.confirm('Tüm limanları silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) {
      handleChange('ports', []);
      toast.success('Tüm limanlar silindi');
    }
  };

  const turkishPortCount = ports.filter(p => p.code.startsWith('TR')).length;
  const otherPortCount = ports.length - turkishPortCount;

  return (
    <div className="card">
      <div className="card-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              <Ship size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Liman Yönetimi
            </h3>
            <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
              {ports.length.toLocaleString('tr-TR')} liman kayıtlı 
              ({turkishPortCount} Türkiye, {otherPortCount} diğer)
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button 
              className="btn btn-primary" 
              onClick={importFromExcel}
              disabled={importing}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Upload size={16} />
              {importing ? 'İçe Aktarılıyor...' : 'Excel\'den İçe Aktar'}
            </button>
            {ports.length > 0 && (
              <button 
                className="btn btn-danger" 
                onClick={clearAllPorts}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <Trash2 size={16} />
                Tümünü Sil
              </button>
            )}
          </div>
        </div>

        {/* Yeni Liman Ekle */}
        <div style={{ 
          background: '#f0fdf4', 
          padding: '1rem', 
          borderRadius: '8px',
          marginBottom: '1.5rem',
          border: '1px solid #86efac'
        }}>
          <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem', color: '#16a34a' }}>
            <Plus size={16} style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} />
            Yeni Liman Ekle
          </h4>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ marginBottom: 0, width: '150px' }}>
              <label className="form-label">Liman Kodu</label>
              <input
                type="text"
                className="form-input"
                value={newPortCode}
                onChange={(e) => setNewPortCode(e.target.value.toUpperCase())}
                placeholder="Örn: TRAMB"
                maxLength={10}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
              <label className="form-label">Liman Adı</label>
              <input
                type="text"
                className="form-input"
                value={newPortName}
                onChange={(e) => setNewPortName(e.target.value)}
                placeholder="Örn: AMBARLI"
              />
            </div>
            <button 
              className="btn btn-success" 
              onClick={addPort}
              disabled={!newPortCode.trim() || !newPortName.trim()}
            >
              <Plus size={16} /> Ekle
            </button>
          </div>
        </div>

        {/* Arama ve Filtre */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              className="form-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Liman kodu veya adı ile ara..."
              style={{ paddingLeft: '40px' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button 
              className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter('all')}
              style={{ minWidth: '80px' }}
            >
              Tümü
            </button>
            <button 
              className={`btn ${filter === 'TR' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter('TR')}
              style={{ minWidth: '100px' }}
            >
              🇹🇷 Türkiye
            </button>
            <button 
              className={`btn ${filter === 'other' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter('other')}
              style={{ minWidth: '80px' }}
            >
              🌍 Diğer
            </button>
          </div>
        </div>

        {/* Liman Listesi */}
        <div style={{ 
          maxHeight: '400px', 
          overflowY: 'auto', 
          border: '1px solid #e2e8f0', 
          borderRadius: '8px' 
        }}>
          {filteredPorts.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
              {ports.length === 0 ? (
                <>
                  <MapPin size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
                  <p>Henüz liman eklenmemiş.</p>
                  <p style={{ fontSize: '0.875rem' }}>Excel'den içe aktarabilir veya manuel ekleyebilirsiniz.</p>
                </>
              ) : (
                <p>Aramanızla eşleşen liman bulunamadı.</p>
              )}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#f8fafc' }}>
                <tr>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', borderBottom: '2px solid #e2e8f0' }}>Kod</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', borderBottom: '2px solid #e2e8f0' }}>Liman Adı</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: 600, fontSize: '0.8rem', borderBottom: '2px solid #e2e8f0', width: '80px' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filteredPorts.slice(0, 100).map((port, index) => (
                  <tr key={port.code} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.5rem 1rem' }}>
                      <code style={{ 
                        background: port.code.startsWith('TR') ? '#dcfce7' : '#f1f5f9', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        fontWeight: 600
                      }}>
                        {port.code}
                      </code>
                    </td>
                    <td style={{ padding: '0.5rem 1rem' }}>{port.name}</td>
                    <td style={{ padding: '0.5rem 1rem', textAlign: 'center' }}>
                      <button 
                        className="action-btn action-btn-danger"
                        onClick={() => removePort(port.code)}
                        title="Sil"
                        style={{ padding: '0.25rem 0.5rem' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {filteredPorts.length > 100 && (
            <div style={{ padding: '0.75rem', textAlign: 'center', background: '#fef3c7', color: '#92400e', fontSize: '0.875rem' }}>
              {filteredPorts.length - 100} liman daha var. Daha fazla görmek için arama yapın.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortSettings;
